import { useRef } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  useToast,
  ButtonGroup,
  Text,
} from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';
import { useDeleteOrgGroup } from 'features/user-group/user-group-details/api/useDeleteOrgGroup';
import { getGroupDeleteBody } from 'features/user-group/user-group-details/messages';

interface GroupDeleteConfirmationProps {
  isOpen: boolean;
  groupId: string;
  groupName?: string;
  memberCount?: number;
  onClose: () => void;
  onSuccess?: () => void;
  backUrl: string;
}

export const GroupDeleteConfirmation = ({
  isOpen,
  groupId,
  groupName,
  memberCount,
  onClose,
  onSuccess,
  backUrl,
}: GroupDeleteConfirmationProps) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();
  const history = useHistory();
  const { mutateAsync: deleteGroupAsync, isLoading } = useDeleteOrgGroup();

  const handleDelete = async () => {
    try {
      await deleteGroupAsync(groupId);
      toast({
        status: 'success',
        description: 'Group deleted',
      });
      onClose();
      if (onSuccess) {
        onSuccess();
      } else {
        history.push(backUrl);
      }
    } catch (error) {
      toast({
        status: 'error',
        description: 'Failed to delete group',
      });
    }
  };

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      autoFocus={false}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize='lg' fontWeight='bold'>
            Delete group
          </AlertDialogHeader>
          <AlertDialogBody>
            Are you sure you want to delete the user group{' '}
            <Text as='span' fontWeight='bold'>
              {groupName}
            </Text>
            ? {getGroupDeleteBody(memberCount)}
          </AlertDialogBody>
          <AlertDialogFooter>
            <ButtonGroup>
              <Button
                ref={cancelRef}
                onClick={onClose}
                variant='ghost'
                isDisabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                colorScheme='red'
                data-testid='delete-button'
                onClick={handleDelete}
                isLoading={isLoading}
              >
                Delete
              </Button>
            </ButtonGroup>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
